import { useState, useEffect } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _map from 'lodash/map';
import isNil from 'lodash/isNil';
import isNaN from 'lodash/isNaN';
import isEmpty from 'lodash/isEmpty';

import { DESCRIPTIONS, RegistrationFilters, RESULT_TYPES_LABEL } from './constants';
import useService from './useService';

const findResultField = (row: any) => {
	if (row.mttid) {
		if (row.rank < row.maxWinners) {
			return { ...row, result: RESULT_TYPES_LABEL.WON };
		}
	} else if (
		((DESCRIPTIONS.ONE_OUT_OF_TWO_WIN === row.description ||
			DESCRIPTIONS.ONE_OUT_OF_FOUR_WIN === row.description ||
			DESCRIPTIONS.ONE_OUT_OF_THREE_WIN === row.description) &&
			row.rank === 1) ||
		(DESCRIPTIONS.TWO_OUT_OF_FOUR_WIN === row.description && row.rank <= 2) ||
		(DESCRIPTIONS.THREE_OUT_OF_FOUR_WIN === row.description && row.rank <= 3)
	) {
		return { ...row, result: RESULT_TYPES_LABEL.WON };
	}

	if (!isNil(row.winnings) && !isNaN(row.winnings) && row.winnings > 0) {
		return { ...row, result: RESULT_TYPES_LABEL.WON };
	}

	return { ...row, result: RESULT_TYPES_LABEL.LOST };
};

const useGameplay = () => {
	const { getRegisteredTournaments } = useService();
	const [gameplayData, setGameplayData] = useState<any>({ data: [], rowCount: 0 });
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [filters, setFilters] = useState<RegistrationFilters | undefined>(undefined);
	const [showLoader, setShowLoader] = useState<boolean>(false);

	const fetchGameplayData = async () => {
		try {
			setShowLoader(true);
			const result = await getRegisteredTournaments(filters || {});

			const formattedData = _map(_get(result, 'data'), (row: object) => {
				let newRow = { ...(row || {}) };
				if (
					((_has(newRow, 'description') || (_has(newRow, 'mttid') && _has(newRow, 'maxWinners'))) &&
						_has(newRow, 'rank')) ||
					filters?.gameName === 'ludonx' ||
					filters?.gameName === 'tcnx'
				) {
					newRow = findResultField(newRow);
				}

				if (_has(newRow, 'prizePot') && _has(newRow, 'minNumberOfPlayers')) {
					return { ...newRow, prizePot: _get(newRow, ['prizePot', Number(_get(newRow, 'minNumberOfPlayers'))], 0) };
				}

				if (_has(newRow, 'prizePot')) {
					if (_has(newRow, 'maxParticipants')) {
						return { ...newRow, prizePot: _get(newRow, ['prizePot', Number(_get(newRow, 'maxParticipants'))], 0) };
					}
					if (_has(newRow, 'minParticipants')) {
						return { ...newRow, prizePot: _get(newRow, ['prizePot', Number(_get(newRow, 'minParticipants'))], 0) };
					}
				}

				return newRow;
			});

			setGameplayData({ data: formattedData, rowCount: result?.rowCount });
			setForceUpdate(true);
		} catch (error: any) {
			setGameplayData({ data: [], rowCount: 0 });
			setForceUpdate(true);
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		if (!isNil(filters)) {
			fetchGameplayData();
		}
	}, [filters]);

	const getGameplayData = () => {
		setForceUpdate(false);
		return gameplayData;
	};

	const handleFilterSubmit = (submittedFilters: RegistrationFilters) => {
		const { tournamentID, registeredFrom, registeredTill, gameName, pwfInviteId, tournamentType } = submittedFilters;

		switch (true) {
			case !isNil(tournamentID) && !isNaN(tournamentID):
				setFilters({ tournamentID, gameName });
				break;
			case !isNil(tournamentType) && !isEmpty(tournamentType) && !isNil(pwfInviteId) && !isEmpty(pwfInviteId):
				setFilters({ pwfInviteId, tournamentType, gameName });
				break;
			case !isNil(registeredFrom) && !isNil(registeredTill):
				setFilters({
					registeredFrom,
					registeredTill,
					gameName,
					...(isEmpty(tournamentType) ? {} : { tournamentType })
				});
				break;
			default:
				break;
		}
	};

	return { getGameplayData, handleFilterSubmit, forceUpdate, showLoader };
};

export default useGameplay;
